import { Controller } from "@hotwired/stimulus"
import Cropper from "cropperjs"
import { Modal } from "bootstrap"
import { removeBackgroundFrom, BackgroundRemovalError } from "../lib/background_removal"
import { isHeic, normalizeImageToJpeg, HEIC_UNSUPPORTED_MESSAGE } from "../lib/image_normalizer"
import { syncGateReason } from "../lib/gate_reason"

const BG_REMOVAL_WAIT_REASON = "Wait for the background removal to finish"

/**
 * Shared crop-modal controller for image-upload slots.
 *
 * Wrap a file input and a shared/image_crop_modal partial:
 *   div[data-controller="photo-crop" data-photo-crop-aspect-ratio-value="1" ...]
 *     input[type=file data-photo-crop-target="input"
 *           data-action="change->photo-crop#fileSelected change->image-preview#preview"]
 *     (optional) input[type=file data-photo-crop-target="output"]  — when absent,
 *                the cropped file is written back into the "input" target itself
 *     <shared/_image_crop_modal partial>  — gl_modal shell carrying the
 *                modal/image/error/bgButton/bgButtonLabel/bgProgress/applyButton targets
 *
 * Apply writes the cropped File into the output input via DataTransfer and
 * dispatches `change` so image-preview / card-dirty react. Export is JPEG,
 * or PNG when the background was removed (JPEG cannot store transparency).
 *
 * iPhone HEIC (#14350): a HEIC selection is re-encoded to JPEG by
 * lib/image_normalizer BEFORE the crop opens — wherever the browser can decode
 * HEIC (Safari) the cropper, background removal and the input itself only ever
 * hold a JPEG, so the raw HEIC never reaches the server even if the crop is
 * never applied. Where it cannot (desktop Chrome/Firefox) the normalizer hands
 * the original back, the cropper's <img> fails to load, and the error names the
 * fix (HEIC_UNSUPPORTED_MESSAGE); cancelling the modal clears the input.
 */
export default class extends Controller {
  static targets = ["input", "output", "modal", "modalTemplate", "image", "error",
    "bgButton", "bgButtonLabel", "bgProgress", "bgProgressLabel", "applyButton"]

  static values = {
    aspectRatio: { type: Number, default: 1 }, // 0 = free-form crop
    outputMaxEdge: { type: Number, default: 800 },
    // Large inputs are fine — we re-encode after cropping. The 2 MB server rule
    // is enforced on the OUTPUT blob instead (outputMaxSize).
    maxSize: { type: Number, default: 20971520 }, // 20 MB input guard
    outputMaxSize: { type: Number, default: 2097152 }, // 2 MB, matches ImageUploadValidation
    // HEIC/HEIF accepted because iPhone Safari can decode it onto the crop
    // canvas; export is JPEG/PNG so HEIC never reaches the server (#6130).
    acceptedTypes: { type: String, default: "image/jpeg,image/png,image/heic,image/heif" },
    bgRemoval: { type: Boolean, default: true },
    // "auto" = JPEG, or PNG once the background is removed. "png" forces PNG
    // for slots whose uploads must keep their alpha channel (company logos):
    // a transparent PNG cropped through the default JPEG export would come
    // back flattened onto a solid background with no error anywhere.
    outputFormat: { type: String, default: "auto" },
  }

  static REMOVE_LABEL = "Remove Background"
  static RESTORE_LABEL = "Restore Background"

  connect() {
    this.mountDeferredModal()
    this.cropper = null
    this.originalFile = null
    // Bumped on every selection; a HEIC normalization that resolves after a
    // newer pick (or a cancel) is discarded instead of opening a stale file.
    this.selection = 0
    this.originalUrl = null
    this.processedUrl = null
    this.backgroundRemoved = false
    this.applied = false
    this.busy = false
    this.suppressNextOpen = false
    this.lastAppliedFiles = null
    // Generation counter — bumped every time the modal closes. apply()
    // captures the current value and a pending toBlob callback checks it
    // before acting, so an Esc/hide that races an in-flight crop can't apply
    // a cancelled crop (M-5).
    this.session = (this.session || 0)
    // Turbo snapshots the outgoing page BEFORE disconnect() fires, so a
    // navigation with the modal still open would cache a live blob: src that
    // revokeUrls() then kills — the cache restore fetches a dead blob.
    // turbo:before-cache is the hook that mutates the DOM ahead of the clone.
    this.beforeCacheHandler = () => this.revokeUrls()
    document.addEventListener("turbo:before-cache", this.beforeCacheHandler)
  }

  // The slot ships the crop modal inert in a <template> (#13905: a response
  // may carry no nested modal markup — the GOAT Store order dialog validates
  // that). Mount it once, inside this controller's scope so every existing
  // `modal`/`image`/… target resolves exactly as it did when it rendered inline.
  mountDeferredModal() {
    if (this.hasModalTarget || !this.hasModalTemplateTarget) return
    this.element.appendChild(this.modalTemplateTarget.content.cloneNode(true))
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCacheHandler)
    this.destroyCropper()
    this.revokeUrls()
  }

  // Returns a promise that settles once the modal has opened (or the
  // selection was rejected) — a JPEG/PNG opens synchronously, a HEIC after
  // normalization. Callers never need the value; tests await it.
  fileSelected(event) {
    if (this.suppressNextOpen) {
      this.suppressNextOpen = false
      return Promise.resolve()
    }
    const file = event.target.files[0]
    if (!file) return Promise.resolve()
    if (!this.validateFile(file)) return Promise.resolve()

    const selection = ++this.selection
    if (!isHeic(file)) {
      this.openCropModal(file)
      return Promise.resolve()
    }
    return normalizeImageToJpeg(file).then((normalized) => {
      if (selection !== this.selection) return
      if (normalized !== file) this.replaceInputFile(normalized)
      this.openCropModal(normalized)
    })
  }

  openCropModal(file) {
    this.originalFile = file
    this.applied = false
    this.clearError()
    this.setBackgroundRemoved(false)
    this.revokeUrls()
    this.originalUrl = URL.createObjectURL(file)
    this.showImage(this.originalUrl)
    this.bootstrapModal.show()
  }

  // The normalized JPEG replaces the raw HEIC in the form input WITHOUT a
  // change event: the host controllers already reacted to the selection (a
  // file is a file to them), image-preview already painted it, and a change
  // here would re-enter fileSelected. An applied crop overwrites it; a cancel
  // clears it (restoreOutputToLastApplied).
  replaceInputFile(file) {
    try {
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(file)
      this.inputTarget.files = dataTransfer.files
    } catch {
      // No DataTransfer — the raw file stays until the crop is applied (JPEG)
      // or cancelled (cleared); the server names the fix if it ever arrives.
    }
  }

  async removeBackground() {
    if (!this.bgRemovalValue) return
    if (this.busy || !this.originalFile) return
    if (this.backgroundRemoved) {
      this.setBackgroundRemoved(false)
      this.showImage(this.originalUrl)
      return
    }
    // Reuse the already-processed result when re-removing after a restore
    // within the same modal session — avoids re-running the ML pass. Cleared
    // by revokeUrls() on a new file selection or on modal close (M-4).
    if (this.processedUrl) {
      this.setBackgroundRemoved(true)
      this.showImage(this.processedUrl)
      return
    }

    // Same stale-continuation guard as apply()'s toBlob callback (M-5): if
    // the modal hides mid-inference, modalHidden() bumps this.session and the
    // resolved result below is discarded instead of mutating the next session
    // (e.g. stamping photo A's cutout into photo B's processedUrl cache).
    const session = this.session
    this.busy = true
    // BUTTON-8: Remove Background is busy (aria-busy keeps its rest paint; the
    // progress line below is its spinner). Its label is not swapped here:
    // setBackgroundRemoved owns it. BUTTON-4: Apply waits, and says why.
    this.bgButtonTarget.disabled = true
    this.bgButtonTarget.setAttribute("aria-busy", "true")
    if (this.hasApplyButtonTarget) syncGateReason(this.applyButtonTarget, { ready: false, reason: BG_REMOVAL_WAIT_REASON })
    this.bgProgressTarget.classList.remove("d-none")
    this.clearError()
    try {
      const blob = await removeBackgroundFrom(this.originalFile, {
        moduleUrl: this.modalTarget.dataset.bgRemovalModuleUrl,
        onProgress: (fraction) => {
          if (session !== this.session) return
          if (this.hasBgProgressLabelTarget) {
            this.bgProgressLabelTarget.textContent = `Removing background… ${Math.round(fraction * 100)}%`
          }
        },
      })
      if (session !== this.session) return
      if (this.processedUrl) URL.revokeObjectURL(this.processedUrl)
      this.processedUrl = URL.createObjectURL(blob)
      this.setBackgroundRemoved(true)
      this.showImage(this.processedUrl)
    } catch (e) {
      if (session !== this.session) return
      // The user-facing message is intentionally generic; the underlying
      // cause must still reach the console or failures are undiagnosable
      // from a user report.
      console.error("Background removal failed:", e)
      const message = e instanceof BackgroundRemovalError ? e.userMessage : "Background removal is unavailable right now."
      this.showError(message)
    } finally {
      this.busy = false
      this.bgButtonTarget.removeAttribute("aria-busy")
      this.bgButtonTarget.disabled = false
      if (this.hasApplyButtonTarget) syncGateReason(this.applyButtonTarget, { ready: true })
      this.bgProgressTarget.classList.add("d-none")
    }
  }

  apply() {
    if (!this.cropper || this.busy) return

    // Captured now — if the modal hides (Esc/backdrop/Cancel) before the
    // async toBlob callback fires, modalHidden() bumps this.session and the
    // callback below bails instead of applying a cancelled crop (M-5).
    const session = this.session

    const canvas = this.cropper.getCroppedCanvas({
      maxWidth: this.outputMaxEdgeValue,
      maxHeight: this.outputMaxEdgeValue,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: "high",
    })

    // PNG when transparent — JPEG has no alpha channel and would paint the
    // removed background black. Also PNG when the slot demands it
    // (outputFormat "png", e.g. transparent company logos). JPEG otherwise:
    // smaller, and transparently converts iPhone HEIC sources, which only
    // Safari can display and the server refuses as uploads (#6130, #14350).
    const usePng = this.backgroundRemoved || this.outputFormatValue === "png"
    const type = usePng ? "image/png" : "image/jpeg"
    const name = usePng ? "profile-photo.png" : "profile-photo.jpg"
    const quality = usePng ? undefined : 0.9

    canvas.toBlob((blob) => {
      if (session !== this.session) return
      if (!blob) {
        this.showError("Something went wrong preparing the image — try a smaller crop or a different photo.")
        return
      }
      if (blob.size > this.outputMaxSizeValue) {
        const maxMb = (this.outputMaxSizeValue / 1024 / 1024).toFixed(0)
        // PNG cutouts can outweigh JPEG — once the background is removed the
        // "remove the background" advice is stale (the button says Restore).
        const advice = this.backgroundRemoved ? "crop a smaller area" : "crop a smaller area or remove the background"
        this.showError(`Cropped image is over ${maxMb} MB — ${advice}.`)
        return
      }
      const file = new File([blob], name, { type })
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(file)
      const target = this.outputInput
      target.files = dataTransfer.files
      this.lastAppliedFiles = dataTransfer.files
      this.applied = true
      if (target === this.inputTarget) this.suppressNextOpen = true
      target.dispatchEvent(new Event("change", { bubbles: true }))
      // Distinct from the change above: consumers that must act ONLY on a
      // finished crop (profile-photo's instant upload) listen for this —
      // a raw change listener can't tell the user's original selection
      // apart from the cropped result written back here.
      target.dispatchEvent(new CustomEvent("photo-crop:applied", { bubbles: true }))
      this.bootstrapModal.hide()
    }, type, quality)
  }

  cancel() {
    this.bootstrapModal.hide() // hidden.bs.modal → modalHidden handles restore
  }

  // hidden.bs.modal — fires for Cancel, the X button, backdrop and Esc, AND
  // after apply(); `applied` distinguishes the two.
  modalHidden() {
    this.session = (this.session || 0) + 1
    this.selection += 1
    this.destroyCropper()
    if (!this.applied) this.restoreOutputToLastApplied()
    this.applied = false
    this.setBackgroundRemoved(false)
    this.revokeUrls()
    this.clearError()
  }

  // ── Private ──────────────────────────────────────────────────────

  get bootstrapModal() {
    return Modal.getOrCreateInstance(this.modalTarget)
  }

  // Every slot today (shared/image_upload_slot, including the onboarding
  // thank-you step) reuses the visible form input as the output; the optional
  // output target remains for a caller that keeps a separate hidden input.
  get outputInput() {
    return this.hasOutputTarget ? this.outputTarget : this.inputTarget
  }

  restoreOutputToLastApplied() {
    const target = this.outputInput
    if (target === this.inputTarget) this.suppressNextOpen = true
    if (this.lastAppliedFiles) {
      target.files = this.lastAppliedFiles
    } else {
      target.value = ""
    }
    if (target !== this.inputTarget) {
      this.inputTarget.value = ""
      this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
    target.dispatchEvent(new Event("change", { bubbles: true }))
  }

  showImage(url) {
    this.imageTarget.onload = () => {
      this.destroyCropper()
      this.cropper = new Cropper(this.imageTarget, {
        aspectRatio: this.aspectRatioValue > 0 ? this.aspectRatioValue : NaN,
        viewMode: 1,
        dragMode: "move",
        autoCropArea: 0.9,
        cropBoxResizable: true,
        cropBoxMovable: true,
        background: false,
        guides: true,
        highlight: true,
        responsive: true,
      })
    }
    this.imageTarget.onerror = () => this.showError(this.undecodableMessage())
    this.imageTarget.src = url
  }

  // A HEIC that is still HEIC here is one the normalizer could not decode
  // either — name the fix; anything else gets the generic wording.
  undecodableMessage() {
    if (isHeic(this.originalFile)) return HEIC_UNSUPPORTED_MESSAGE
    return "That image couldn't be opened — try a JPG or PNG."
  }

  setBackgroundRemoved(removed) {
    this.backgroundRemoved = removed
    this.modalTarget.classList.toggle("gl-crop--transparent", removed)
    if (this.hasBgButtonLabelTarget) {
      this.bgButtonLabelTarget.textContent = removed ? this.constructor.RESTORE_LABEL : this.constructor.REMOVE_LABEL
    }
  }

  validateFile(file) {
    const accepted = this.acceptedTypesValue.split(",").map((t) => t.trim())
    // iPhone HEIC files sometimes report an empty MIME type, so also accept by
    // extension. Decoding is exercised when the cropper loads the image (#6130).
    const isHeicByName = /\.hei[cf]$/i.test(file.name || "")

    if (!accepted.includes(file.type) && !isHeicByName) {
      this.showError(`Please select a ${this.acceptedLabel} image.`)
      this.inputTarget.value = ""
      return false
    }

    if (file.size > this.maxSizeValue) {
      const maxMb = (this.maxSizeValue / 1024 / 1024).toFixed(0)
      this.showError(`Image must be under ${maxMb} MB.`)
      this.inputTarget.value = ""
      return false
    }

    return true
  }

  get acceptedLabel() {
    const names = { "image/jpeg": "JPG", "image/png": "PNG", "image/webp": "WebP", "image/heic": "HEIC", "image/heif": "HEIC" }
    const labels = [...new Set(this.acceptedTypesValue.split(",").map((t) => names[t.trim()]).filter(Boolean))]
    if (!labels.length) return "supported"
    return labels.length > 1 ? `${labels.slice(0, -1).join(", ")}, or ${labels.at(-1)}` : labels[0]
  }

  // A photo-crop scope can carry TWO error targets — a page-level one (shown
  // for pre-modal validation, e.g. bad type / oversized input) and an
  // in-modal one (shown for in-modal failures, e.g. bg-removal or oversized
  // output) — because the modal itself may be hidden (closed) or the
  // page-level div may sit behind an open modal's backdrop. Stimulus's
  // singular errorTarget only returns the FIRST match in DOM order, silently
  // dropping the other, so write to/clear ALL declared error targets (F-1/F-2).
  showError(message) {
    this.errorTargets.forEach((target) => {
      target.textContent = message
      target.classList.remove("d-none")
    })
  }

  clearError() {
    this.errorTargets.forEach((target) => {
      target.textContent = ""
      target.classList.add("d-none")
    })
  }

  destroyCropper() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
  }

  revokeUrls() {
    if (this.originalUrl) { URL.revokeObjectURL(this.originalUrl); this.originalUrl = null }
    if (this.processedUrl) { URL.revokeObjectURL(this.processedUrl); this.processedUrl = null }
    // A revoked blob: src left on the img gets snapshotted by Turbo's page
    // cache and 404s (net::ERR_FILE_NOT_FOUND) when the page is restored.
    if (this.hasImageTarget && this.imageTarget.src.startsWith("blob:")) {
      this.imageTarget.removeAttribute("src")
    }
  }
}
